import { ApolloError } from '@apollo/client';

import Logger from '~c/logger';

const log = Logger('helpers/apollo-error');

export interface ResultError {
  message?: string;
  statusCode?: number;
}

/**
 * Parse Apollo Error and return custom ResultError object
 * For details on how to parse ApolloError,
 * See: https://www.apollographql.com/docs/apollo-server/data/errors/
 *
 * @param error ApolloError object
 */
export function parseApolloError(error: ApolloError | string) {
  let stacktrace: string[] | undefined;
  const parsedError: ResultError = {};

  if (typeof error === 'string') {
    parsedError.message = error;
    return parsedError;
  }

  const { graphQLErrors, networkError } = error;
  if (graphQLErrors && graphQLErrors.length > 0) {
    // Each graphQLErrors contains a structure like:
    // {
    //   extensions: { code: 'INTERNAL_SERVER_ERROR', stacktrace: [] },
    //   message: 'error message',
    //   path: ['clientFolder', 'primary'],
    // }
    // Just process the first error, the rest will be logged.
    const firstError = graphQLErrors[0];
    parsedError.message = firstError.message;
    stacktrace = (firstError.extensions?.stacktrace as string[]) ?? [];
  } else if (networkError) {
    const { result, statusCode, stack } = networkError as any;
    parsedError.statusCode = statusCode;
    stacktrace = stack.split('\n');
    if (statusCode === 401) {
      // access token revoked (or user not logged in)
      parsedError.message =
        'Your session has expired. Please log in again to continue.';
      // Redirect to the sign-in page with referrer to return after login
      if (typeof window !== 'undefined') {
        const isAlreadyOnSignIn =
          window.location.pathname.startsWith('/sign-in');

        // Don't redirect if already on sign-in page (prevents infinite loop)
        if (isAlreadyOnSignIn) {
          return parsedError;
        }

        // Store current path so user can return after login
        // Only use pathname, not search params (prevents nested referrer bug)
        const currentPath = window.location.pathname;
        const qs = new URLSearchParams({ referrer: currentPath });
        // Small delay to allow any save operations to show error message
        setTimeout(() => {
          window.location.href = `/sign-in?${qs.toString()}`;
        }, 2000);
      }
    } else if (result) {
      const { errors, message } = result;
      if (message) {
        parsedError.message = message;
      } else if (errors && errors.length > 0) {
        parsedError.message = errors.map((err: any) => err.message).join('\n');
      }
    }
  }

  if (!parsedError.message && error.message) {
    parsedError.message = error.message;
  }

  log.error({ parsedError, graphQLErrors, networkError });
  // Display stacktrace directly to console for easy reading
  if (stacktrace) {
    console.warn(stacktrace.join('\n'));
  }
  return parsedError;
}
